import React, { useState, useEffect } from "react";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import { backendURL } from "../../constants";
import {
  Grid,
  Box,
  Stack,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import SectionTitle from "./SectionTitle";
import { timelineSx } from "./timelineSx";
import ActionButton from "./ActionButton";
import NavButton from "./NavButton";
import DeleteButton from "./DeleteButton";
import {
  FIX_PREDICTION,
  RESET_BOX,
  GET_IMAGE_ANALYSES,
  GET_IMAGE_BOX,
  GET_PATTERN_ANALYSES,
  SEND_TO_TRAINING,
  USED_IN_TRAINING,
  tokenOptions,
} from "../../backend/graphql";
import { useMutation, useQuery } from "@apollo/client";
import { useAppSelector } from "../../app/hooks";
import { seesAdminData } from "../../app/features/auth/authTypes";
import BoundingBoxImage from "./BoundingBoxImage";
import ColorSample from "./ColorSample";
import ZoomableImage from "./ZoomableImage";
import { isAnalyzed, isPattern } from "../../backend/calibration";

// The card's reference colors, in the order they are printed and read.
const CARD_COLORS = ["C", "M", "Y", "K", "W"];

type ImageAnalysisProps = {
  imageID: String;
  calibration?: string | null;
};

const ImageAnalysis = ({ imageID, calibration }: ImageAnalysisProps) => {
  const token = useAppSelector((state) => state.auth.token);
  const role = useAppSelector((state) => state.auth.role);
  const [fixPrediction] = useMutation(FIX_PREDICTION, tokenOptions(token));
  const [sendToTraining] = useMutation(SEND_TO_TRAINING, tokenOptions(token));
  const [resetBox] = useMutation(RESET_BOX, tokenOptions(token));
  const [confirmResetBox, setConfirmResetBox] = useState(false);
  // The admin steps show to whoever sees admin data; their buttons only to an admin.
  const seesAll = seesAdminData(role);
  const canEdit = role === "Admin";
  const [recalibrating, setRecalibrating] = useState<boolean>(false);
  const [boxPending, setBoxPending] = useState<boolean>(false);
  const [detourPending, setDetourPending] = useState<boolean>(false);
  const [hasCalibrated, setHasCalibrated] = useState<boolean>(false);

  const { loading, error, data, refetch } = useQuery(
    GET_IMAGE_ANALYSES,
    {
      ...tokenOptions(token),
      variables: {
        take: 1,
        skip: 0,
        filter: 'image.iD == "' + imageID + '"',
      },
    }
  );

  // Whether this image has already been sent to mask/detour training, for the 🎓 pictogram
  // next to those stages. Shown to whoever sees admin data, like the steps it labels.
  const { data: usageData } = useQuery(USED_IN_TRAINING, {
    ...tokenOptions(token),
    variables: { imageID },
    skip: !seesAll,
  });
  const usage = usageData?.usedInTraining;

  // Only an original is analyzed, so every other picture — a pattern, a web or a web-like one — has
  // no ImageAnalysis and neither its box nor its error arrives through the query above. Read the
  // image row itself for those.
  const pattern = isPattern(calibration);
  const analyzed = isAnalyzed(calibration);
  const { data: patternBoxData } = useQuery(GET_IMAGE_BOX, {
    ...tokenOptions(token),
    variables: { take: 1, skip: 0, filter: 'iD == "' + imageID + '"' },
    skip: analyzed,
  });
  const imageRow = patternBoxData?.images?.items[0];

  // What the pattern was read from: its detour and the colors measured on it.
  const { data: patternData } = useQuery(GET_PATTERN_ANALYSES, {
    ...tokenOptions(token),
    variables: { take: 1, skip: 0, filter: 'imageID == "' + imageID + '"' },
    skip: !pattern,
  });
  const patternAnalysis = patternData?.patternAnalyses?.items[0];

  // The backend answers with the calibrated image or refuses, which is all we need to
  // know: patterns and originals have one, the web variants never do. The browser
  // reuses the response for the image below.
  useEffect(() => {
    let active = true;
    fetch(`${backendURL}/image/calibrated/${imageID}`)
      .then((response) => {
        if (active) setHasCalibrated(response.ok);
      })
      .catch(() => {
        if (active) setHasCalibrated(false);
      });
    return () => {
      active = false;
    };
  }, [imageID]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  const analysis = data?.imageAnalyses?.items[0];
  const analysisId = analysis?.id;

  // The two moves an original was calibrated on: A (background) onto B (white), C (stone) onto
  // Predicted, the color the fakecalibration gives it.
  let calibrationColors: Record<string, number[]> = {};
  try {
    calibrationColors = analysis?.calibrationColors
      ? JSON.parse(analysis.calibrationColors)
      : {};
  } catch {
    calibrationColors = {};
  }

  const image = analysis?.image;
  const hasBox =
    image?.boxMinX != null &&
    image?.boxMinY != null &&
    image?.boxMaxX != null &&
    image?.boxMaxY != null;

  // The best picture of this stone there is — which is what the pipeline itself works
  // from, so the box is drawn on the image it was found in.
  const bestImageUrl = hasCalibrated
    ? `${backendURL}/image/calibrated/${imageID}`
    : `${backendURL}/image/${imageID}`;

  const rawImageUrl = `${backendURL}/image/${imageID}`;

  // A pattern's box is stored in raw-photo pixels (the calibrated pattern is a crop, so the box
  // only lines up on the raw image).
  const hasPatternBox =
    imageRow?.boxMinX != null &&
    imageRow?.boxMinY != null &&
    imageRow?.boxMaxX != null &&
    imageRow?.boxMaxY != null;

  // Throw the box away and let the calibration find it again — the one thing a plain recalibration
  // deliberately leaves alone, since every later stage is cut from it. Asked for twice: a new box
  // shifts the clip, and every sample corrected against the old one stops matching it.
  const handleResetBox = () => {
    setConfirmResetBox(false);
    setRecalibrating(true);
    resetBox({ variables: { imageId: imageID } })
      .then(() => window.location.reload())
      .catch(() => setRecalibrating(false));
  };

  // Improve box seeds the sample if needed, then opens its editor on the result.
  const handleImproveBox = () => {
    setBoxPending(true);
    sendToTraining({ variables: { imageID, stage: "box" } })
      .then((res) => {
        const id = res.data?.sendToTraining?.id;
        if (id) window.open(`/learning/box/edit/${id}`, "_blank");
      })
      .finally(() => setBoxPending(false));
  };

  // Same for the detour: seed the golden from the current outline if needed, then edit it.
  const handleImproveDetour = () => {
    setDetourPending(true);
    sendToTraining({ variables: { imageID, stage: "detour" } })
      .then((res) => {
        const id = res.data?.sendToTraining?.id;
        if (id) window.open(`/learning/detour/edit/${id}`, "_blank");
      })
      .finally(() => setDetourPending(false));
  };

  const imageWidth = "256px";

  const rawImageStep = (last: boolean) => (
    <TimelineItem>
      <TimelineOppositeContent />
      <TimelineSeparator>
        <TimelineDot color={imageID !== null ? "primary" : undefined} />
        {!last && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <Grid container direction="row">
          <Grid item xs={4}>
            Raw image
          </Grid>
          <Grid item xs={8}>
            <ZoomableImage
              width={imageWidth}
              src={`${backendURL}/image/${imageID}`}
            />
          </Grid>
        </Grid>
      </TimelineContent>
    </TimelineItem>
  );

  // The raw image is right above, so the calibration step shows only its own result.
  // The original's timeline shows only the two color moves; the calibrated picture itself is the
  // pattern's to show.
  const calibrationStep = (
    last: boolean,
    showPicture: boolean = true,
    label: string = "Calibration",
  ) => (
    <TimelineItem>
      <TimelineOppositeContent />
      <TimelineSeparator>
        <TimelineDot color="primary" />
        {!last && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <Grid container direction="row">
          <Grid item xs={4}>
            {label}
          </Grid>
          <Grid item xs={8}>
            <Stack spacing={1} alignItems="flex-start">
              {showPicture && (
                <ZoomableImage
                  width={imageWidth}
                  src={`${backendURL}/image/calibrated/${imageID}`}
                />
              )}
              {/* The two moves the calibration made: the background onto the shared white, and
                  the stone onto the color the fakecalibration predicted — each square the color
                  as photographed (top left) against what it became (bottom right). */}
              {calibrationColors.A && (
                <Stack direction="row" spacing={2}>
                  <Stack alignItems="center" spacing={0.5}>
                    <ColorSample
                      rgb={calibrationColors.A}
                      splitRgb={calibrationColors.B}
                      size={56}
                    />
                    <Box>Background</Box>
                  </Stack>
                  <Stack alignItems="center" spacing={0.5}>
                    <ColorSample
                      rgb={calibrationColors.C}
                      splitRgb={calibrationColors.Predicted}
                      size={56}
                    />
                    <Box>Stone</Box>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
      </TimelineContent>
    </TimelineItem>
  );

  // A pattern's stone is boxed by the same model as an original's; the box is stored in raw-photo
  // pixels and drawn on the raw pattern. Admins can correct it, exactly like an original's box.
  const patternBoxStep = (last: boolean) => (
    <TimelineItem>
      <TimelineOppositeContent />
      <TimelineSeparator>
        <TimelineDot color={hasPatternBox ? "primary" : undefined} />
        {!last && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <Grid container direction="row">
          <Grid item xs={4}>
            Bounding box{" "}
            {usage?.box && <span title="Used in box training">🎓</span>}
          </Grid>
          <Grid item xs={8}>
            <Stack spacing={1} alignItems="flex-start">
              {hasPatternBox && (
                <BoundingBoxImage
                  imageUrl={rawImageUrl}
                  minx={imageRow.boxMinX}
                  miny={imageRow.boxMinY}
                  maxx={imageRow.boxMaxX}
                  maxy={imageRow.boxMaxY}
                  width={imageWidth}
                />
              )}
              {canEdit && (
                <Stack direction="row" spacing={1}>
                  <NavButton
                    size="small"
                    disabled={boxPending}
                    onClick={handleImproveBox}
                  >
                    Improve box
                  </NavButton>
                  {/* As on an original: a recalibration keeps the box on purpose, so the pattern is
                      clipped the same way every time; this is how it is let go. */}
                  <ActionButton
                    size="small"
                    disabled={recalibrating}
                    onClick={() => setConfirmResetBox(true)}
                  >
                    Reset box
                  </ActionButton>
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>
      </TimelineContent>
    </TimelineItem>
  );

  // The stone's outline, the same step for an original (on its clip) and a pattern (on its
  // calibrated picture). Admins can correct it: the correction is a training sample, which the next
  // recalibration or analysis uses in place of the models.
  const detourStep = (detourID: string | null | undefined, last: boolean) => (
    <TimelineItem>
      <TimelineOppositeContent />
      <TimelineSeparator>
        <TimelineDot color={detourID ? "primary" : undefined} />
        {!last && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <Grid container direction="row">
          <Grid item xs={4}>
            Detour{" "}
            {usage?.detour && <span title="Used in detour training">🎓</span>}
          </Grid>
          <Grid item xs={8}>
            <Stack spacing={1} alignItems="flex-start">
              {detourID && (
                <ZoomableImage
                  width={imageWidth}
                  src={`${backendURL}/image/${detourID}`}
                />
              )}
              {/* Only where there is a detour to improve: the training sample is the detour
                  recomputed from the clip, so on a stone the models find no outline in, the
                  button has nothing to send and quietly does nothing. */}
              {canEdit && detourID && (
                <NavButton
                  size="small"
                  disabled={detourPending}
                  onClick={handleImproveDetour}
                >
                  Improve detour
                </NavButton>
              )}
            </Stack>
          </Grid>
        </Grid>
      </TimelineContent>
    </TimelineItem>
  );

  // The colors a pattern was read from, as RGB triples keyed by name.
  let patternColors: Record<string, any> = {};
  try {
    patternColors = patternAnalysis?.colors ? JSON.parse(patternAnalysis.colors) : {};
  } catch {
    patternColors = {};
  }

  // The card's five printed references: each square holds the color as it came out on the photo
  // (top left) against the target the calibration corrected it to (bottom right).
  const patternTargets: Record<string, number[]> = patternColors.Targets ?? {};

  const cardColorsStep = (last: boolean) => (
    <TimelineItem>
      <TimelineOppositeContent />
      <TimelineSeparator>
        <TimelineDot color={patternColors.W ? "primary" : undefined} />
        {!last && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <Grid container direction="row">
          <Grid item xs={4}>
            Card colors
          </Grid>
          <Grid item xs={8}>
            <Stack direction="row" spacing={1}>
              {CARD_COLORS.filter((key) => patternColors[key]).map((key) => (
                <Stack key={key} alignItems="center" spacing={0.5}>
                  <ColorSample
                    rgb={patternColors[key]}
                    splitRgb={patternTargets[key]}
                    size={56}
                  />
                  <Box>{key}</Box>
                </Stack>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </TimelineContent>
    </TimelineItem>
  );

  // Resetting the box is not undoable: a new box shifts the clip, and every sample corrected
  // against the old one — the box golden, the detour golden — stops matching it.
  const resetBoxDialog = (
    <Dialog open={confirmResetBox} onClose={() => setConfirmResetBox(false)}>
      <DialogTitle>Reset the clipping box?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          If you reset the clipping box, all training done on this stone will be lost.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmResetBox(false)}>Cancel</Button>
        <DeleteButton onClick={handleResetBox}>Reset box</DeleteButton>
      </DialogActions>
    </Dialog>
  );

  // Only originals are analyzed. Anything else shows what the backend could produce
  // from the image alone — a pattern is calibrated, a web image is not — and stops
  // there, rather than listing steps that will never run.
  if (!analyzed) {
    // A pattern shows how it was read: raw → bounding box → the card's colors → calibration →
    // detour. Anything else (the web variants) just shows what the backend could
    // produce from the image alone.
    return (
      <>
        <SectionTitle>Calibration</SectionTitle>
        {pattern && resetBoxDialog}
        {/* The same warning the analyzed pictures show, from where these carry it: a picture with no
            ImageAnalysis records a failed run on the image row itself. */}
        {imageRow?.isError && (
          <Alert severity="error" sx={{ m: 1 }}>
            An error occurred when processing this image.
          </Alert>
        )}
        <Timeline sx={timelineSx}>
          {rawImageStep(!pattern && !hasCalibrated)}
          {/* The box and the calibrated picture are how the pattern was read — for those who see admin data
              to look at (an admin also corrects them); an expert sees what was read off it. */}
          {pattern && seesAll && patternBoxStep(false)}
          {pattern && cardColorsStep(false)}
          {/* A web image's calibrated version is the picture given its drop shadow. */}
          {hasCalibrated &&
            (!pattern || seesAll) &&
            calibrationStep(
              !pattern,
              true,
              calibration?.endsWith("Web") ? "With shadow" : "Calibration",
            )}
          {pattern && detourStep(patternAnalysis?.detourID, true)}
        </Timeline>
      </>
    );
  }

  return (
    <>
      <SectionTitle>Calibration</SectionTitle>
      {resetBoxDialog}

      {/* The original's reading fell over: a fault to look at, or a picture to retake. */}
      {analysis?.isError && (
        <Alert severity="error" sx={{ m: 1 }}>
          An error occurred when reading this picture.
        </Alert>
      )}

      <Timeline sx={timelineSx}>
        {rawImageStep(false)}
        {/* The calibrated picture itself is for those who see admin data; an expert sees the color moves. */}
        {hasCalibrated && calibrationStep(false, seesAll)}

        {/* The box is for those who see admin data (an admin also corrects it); an expert sees what was
            read inside it. */}
        {seesAll && (
          <TimelineItem>
            <TimelineOppositeContent />
            <TimelineSeparator>
              <TimelineDot color={hasBox ? "primary" : undefined} />
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <Grid container direction="row">
                <Grid item xs={4}>
                  Bounding box{" "}
                  {usage?.box && <span title="Used in box training">🎓</span>}
                </Grid>
                <Grid item xs={8}>
                  <Stack spacing={1} alignItems="flex-start">
                    {hasBox && (
                      <BoundingBoxImage
                        imageUrl={bestImageUrl}
                        minx={image.boxMinX}
                        miny={image.boxMinY}
                        maxx={image.boxMaxX}
                        maxy={image.boxMaxY}
                        width={imageWidth}
                      />
                    )}
                    {canEdit && (
                      <Stack direction="row" spacing={1}>
                        <NavButton
                          size="small"
                          disabled={boxPending}
                          onClick={handleImproveBox}
                        >
                          Improve box
                        </NavButton>
                        {/* A recalibration keeps the box on purpose; this is how it is let go. */}
                        <ActionButton
                          size="small"
                          disabled={recalibrating}
                          onClick={() => setConfirmResetBox(true)}
                        >
                          Reset box
                        </ActionButton>
                      </Stack>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </TimelineContent>
          </TimelineItem>
        )}

        {/* The vet and the clip are for those who see admin data; an expert sees what was read. */}
        {seesAll && (
          <>
            <TimelineItem>
              <TimelineOppositeContent />
              <TimelineSeparator>
                <TimelineDot
                  color={
                    analysis?.isPredictedValid != null ? "primary" : undefined
                  }
                />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Grid container direction="row">
                  <Grid item xs={4}>
                    Validate image content
                  </Grid>
                  <Grid item xs={8}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box>
                        {analysis?.isPredictedValid !== null &&
                        analysis?.isPredictedValid !== undefined
                          ? analysis.isPredictedValid === true
                            ? "✅"
                            : "❌  Bad image"
                          : null}
                      </Box>
                      {/* Only once there is a verdict to overrule. Until the analysis has run there is
                          nothing to flip, and a recalibration blanks the verdict until the next one. */}
                      {canEdit && analysis?.isPredictedValid != null && (
                        <ActionButton
                          size="small"
                          // The override, and nothing else. It used to leave a training sample behind
                          // as well, from when a model decided this; the answer is a rule now and the
                          // override is simply recorded.
                          onClick={() =>
                            fixPrediction({
                              variables: { id: analysisId },
                            }).then(() => refetch())
                          }
                        >
                          Fix validity
                        </ActionButton>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </TimelineContent>
            </TimelineItem>

            <TimelineItem>
              <TimelineOppositeContent />
              <TimelineSeparator>
                <TimelineDot
                  color={analysis?.clipID ? "primary" : undefined}
                />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Grid container direction="row">
                  <Grid item xs={4}>
                    Clip
                  </Grid>
                  <Grid item xs={8}>
                    {analysis?.clipID && (
                      <ZoomableImage
                        width={imageWidth}
                        src={`${backendURL}/image/${analysis.clipID}`}
                      />
                    )}
                  </Grid>
                </Grid>
              </TimelineContent>
            </TimelineItem>
          </>
        )}

        {detourStep(analysis?.detourID, true)}
      </Timeline>
    </>
  );
};

export default ImageAnalysis;
